/**
 * Curated featured projects — the hand-picked block at the top of the
 * Projects panel.
 *
 * Why this exists: the panel below fetches the GitHub API live, which means
 * it can only ever show public repositories, and a couple of the featured
 * repos have no GitHub description at all. This file supplies the curated
 * one-liner + stack for both cases.
 *
 * Rules:
 *   - public entries carry `repo` (must match the GitHub repo name) and `url`
 *   - private entries carry `private: true`, no link, and never name a client
 *     or employer — the work is described, not disclosed
 *
 * All copy is derived from the profile README, the single source of truth:
 * https://github.com/AJun01/AJun01
 */

export const FEATURED = [
  {
    id: 'backlot',
    name: 'Backlot Film Maker',
    repo: 'Backlot-Film-Maker',
    url: 'https://github.com/AJun01/Backlot-Film-Maker',
    tagline:
      'Agentic film production toolchain — a canonical ProductionBible drives every agent from script breakdown to rendered shots.',
    stack: ['Python', 'FastAPI', 'React', 'Redis', 'Qdrant', 'Neo4j', 'LlamaIndex'],
  },
  {
    id: 'ow-socrates',
    name: 'OW-Socrates',
    repo: 'ow-socrates',
    url: 'https://github.com/AJun01/ow-socrates',
    tagline:
      'A Socratic hint assistant that refuses to spoil Outer Wilds — QLoRA-tuned Qwen3-8B served locally on a 16 GB M2 Pro.',
    stack: ['Python 3.12', 'MLX-LM', 'QLoRA', 'Qwen3-8B'],
  },
  {
    id: 'sleeping-iron',
    name: 'Sleeping Iron HD-2D',
    repo: 'godot-HD-SleepingIron',
    url: 'https://github.com/AJun01/godot-HD-SleepingIron',
    tagline:
      'My original sci-fi mecha novel rebuilt as an HD-2D Godot game, with spec-driven development and headless CI enforced end to end.',
    stack: ['Godot 4.7', 'GDScript', 'GitHub Actions'],
  },
  {
    id: 'resume-tailor',
    name: 'Resume Tailor',
    repo: 'master-resume',
    url: 'https://github.com/AJun01/master-resume',
    tagline:
      'Conservative resume micro-tuning: a ~30% change budget, a JD-cited reason per change, ATS quality gates and diff-first output.',
    stack: ['TypeScript', 'Next.js', 'Drizzle', 'PostgreSQL', 'Claude'],
  },
  {
    id: 'synab',
    name: 'SYNAB LLC',
    repo: 'SYNAB-LLC',
    url: 'https://github.com/AJun01/SYNAB-LLC',
    demo: 'https://synab-llc.vercel.app',
    tagline:
      'Production marketing site for a cross-border commerce infrastructure firm.',
    stack: ['React 18', 'Vite 6', 'Tailwind v4', 'Framer Motion'],
  },
  {
    id: 'zentrade',
    name: 'ZenTrade',
    private: true,
    tagline:
      'An AI trading coach with enforcement power: seven risk rules escalate from a nudge to a position lock between the impulse and the order.',
    stack: ['TypeScript', 'Node 22', 'SQLite', 'Gemini / Anthropic', 'React', 'MCP'],
  },
  {
    id: 'cua-backend',
    name: 'Computer-Use Agent Backend',
    private: true,
    tagline:
      'FastAPI service running many independent Claude computer-use sessions, each with its own virtual desktop and SSE event stream.',
    stack: ['Python', 'FastAPI', 'PostgreSQL', 'Docker Compose'],
  },
  {
    id: 'veer',
    name: 'VEer',
    private: true,
    tagline:
      'Distills a real education consultant into a 24/7 AI: 100% AWS CDK infrastructure and a six-layer capability runtime.',
    stack: ['TypeScript', 'AWS CDK', 'FastAPI', 'Aurora', 'React 19', 'TanStack Start', 'Stripe'],
  },
];

/**
 * repo name → curated description. Used to replace (or fill in) the GitHub
 * description on the repo cards in the "all repositories" list.
 */
export const FEATURED_REPO_DESC = Object.fromEntries(
  FEATURED.filter((p) => p.repo).map((p) => [p.repo, p.tagline])
);
